import React, { useState, useCallback } from 'react';
import {
  Upload,
  FileCheck,
  AlertTriangle,
  Play,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft,
  Download,
  Loader2
} from 'lucide-react';
import clsx from 'clsx';

import FileUploadZone from './FileUploadZone';
import ValidationSummary from './ValidationSummary';
import ImportPreviewTable from './ImportPreviewTable';
import Button from '@components/common/Button';
import { showToast } from '@utils/toast';
import { ApiError } from '@app-types';
import type {
  ImportRow,
  ImportPreview,
  ImportConflict,
  ValidationError,
  RowStatus,
  RowAction,
  ContractImportData
} from '@app-types/import';
import { importService } from '@services';
import styles from './ManualImport.module.css';

type FilterType = 'all' | 'valid' | 'warning' | 'error' | 'conflict';
type Step = 'upload' | 'validating' | 'preview' | 'importing' | 'complete' | 'error';

interface ImportState {
  step: Step;
  file: File | null;
  preview: ImportPreview | null;
  error: string | null;
  importResult: {
    created: number;
    updated: number;
    skipped: number;
    errors: number;
    importId?: number;
    errorDetails?: Array<{
      rowNumber: number;
      error: string;
    }>;
  } | null;
}

const STEPS = [
  { key: 'upload', label: 'Upload File', number: 1 },
  { key: 'preview', label: 'Review & Validate', number: 2 },
  { key: 'importing', label: 'Import', number: 3 },
  { key: 'complete', label: 'Complete', number: 4 }
];

const ManualImport: React.FC = () => {
  const [state, setState] = useState<ImportState>({
    step: 'upload',
    file: null,
    preview: null,
    error: null,
    importResult: null
  });

  const [filter, setFilter] = useState<FilterType>('all');
  const [rowOverrides, setRowOverrides] = useState<Record<number, RowAction>>({});

  // Handle action change for a specific row
  const handleActionChange = useCallback((rowNumber: number, action: RowAction) => {
    setRowOverrides((prev) => ({
      ...prev,
      [rowNumber]: action
    }));
  }, []);

  // Check if all error rows are skipped
  const areAllErrorsSkipped = useCallback(() => {
    if (!state.preview) return true;

    const errorRows = state.preview.rows.filter(
      (row) => row.status === 'error' || row.status === 'system_critical_error'
    );

    // If no error rows, return true
    if (errorRows.length === 0) return true;

    // Check if all error rows have effective action = 'skip'
    return errorRows.every((row) => {
      const effectiveAction = rowOverrides[row.rowNumber] || row.action;
      return effectiveAction === 'skip';
    });
  }, [state.preview, rowOverrides]);

  // Calculate effective counts based on overrides
  const getEffectiveCounts = useCallback(() => {
    if (!state.preview) return { createCount: 0, updateCount: 0, skipCount: 0 };

    let createCount = 0;
    let updateCount = 0;
    let skipCount = 0;

    state.preview.rows.forEach((row) => {
      // Skip rows with errors - they can't be imported
      if (row.status === 'error' || row.status === 'system_critical_error') {
        skipCount++;
        return;
      }

      const effectiveAction = rowOverrides[row.rowNumber] || row.action;

      switch (effectiveAction) {
        case 'create':
          createCount++;
          break;
        case 'update':
          updateCount++;
          break;
        case 'skip':
          skipCount++;
          break;
      }
    });

    return { createCount, updateCount, skipCount };
  }, [state.preview, rowOverrides]);

  // Get current step number for progress indicator
  const getCurrentStepNumber = () => {
    switch (state.step) {
      case 'upload':
        return 1;
      case 'validating':
        return 1;
      case 'preview':
        return 2;
      case 'importing':
        return 3;
      case 'complete':
        return 4;
      case 'error':
        return state.preview ? 2 : 1;
      default:
        return 1;
    }
  };

  // Validate file with backend API
  const handleFileSelect = useCallback(async (file: File) => {
    setState((prev) => ({
      ...prev,
      file,
      error: null,
      step: 'validating'
    }));

    try {
      // Backend will use saved config from settings
      const preview = await importService.validateImportFile(file);

      // Transform backend response to match our types
      const transformedPreview: ImportPreview = {
        summary: {
          totalRows: preview.totalRows || 0,
          validCount: preview.validation?.validCount || 0,
          warningCount: preview.validation?.warningCount || 0,
          errorCount: preview.validation?.errorCount || 0,
          conflictCount: preview.conflicts?.conflictCount || 0,
          createCount: preview.conflicts?.createCount || 0,
          updateCount: preview.conflicts?.updateCount || 0,
          skipCount: preview.conflicts?.skipCount || 0
        },
        rows: (preview.rows || []).map(
          (row: {
            rowNumber: number;
            data: Record<string, string>;
            status?: string;
            isValid?: boolean;
            action?: string;
            actionReason?: string;
            conflicts?: unknown[];
            errors?: ValidationError[];
            warnings?: ValidationError[];
            assignDefaultOwner?: boolean;
            defaultOwnerId?: number;
            assignDefaultManager?: boolean;
            defaultManagerId?: number;
            terminateAssessments?: boolean;
            createUnknownContacts?: boolean;
          }): ImportRow => ({
            rowNumber: row.rowNumber,
            data: row.data as unknown as ContractImportData,
            status: (row.status as RowStatus) || (row.isValid ? 'valid' : 'error'),
            action: (row.action as RowAction) || 'create',
            actionReason: row.actionReason,
            conflicts: (row.conflicts as unknown as ImportConflict[]) || [],
            errors: row.errors || [],
            warnings: row.warnings || [],
            // Conflict resolution flags
            assignDefaultOwner: row.assignDefaultOwner,
            defaultOwnerId: row.defaultOwnerId,
            assignDefaultManager: row.assignDefaultManager,
            defaultManagerId: row.defaultManagerId,
            terminateAssessments: row.terminateAssessments,
            createUnknownContacts: row.createUnknownContacts
          })
        ),
        newSuppliers: preview.newSuppliers || [],
        newReferenceData: Object.entries(preview.newReferenceData || {})
          .filter(([_, values]) => (values as string[]).length > 0)
          .map(([type, values]) => ({
            type: type as 'cms_contract_status' | 'contract_type' | 'category',
            values: values as string[]
          })),
        appliedConfig: preview.appliedConfig,
        // Include file info for import history
        fileName: file.name,
        fileSize: file.size
      };

      setState((prev) => ({
        ...prev,
        step: 'preview',
        preview: transformedPreview
      }));

      showToast.success(`File validated: ${transformedPreview.summary.totalRows} rows found`);
    } catch (err) {
      const error = err as ApiError;
      const errorMessage =
        (typeof error?.response?.data?.error === 'string' ? error?.response?.data?.error : null) ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to validate file';
      setState((prev) => ({
        ...prev,
        step: 'error',
        error: errorMessage
      }));
      showToast.error(errorMessage);
    }
  }, []);

  const handleFileRemove = useCallback(() => {
    setState({
      step: 'upload',
      file: null,
      preview: null,
      error: null,
      importResult: null
    });
    setFilter('all');
    setRowOverrides({});
  }, []);

  const handleStartImport = useCallback(async () => {
    if (!state.preview) return;

    setState((prev) => ({ ...prev, step: 'importing', error: null }));

    try {
      // Pass row overrides to execute import
      const result = await importService.executeImport(
        state.preview,
        rowOverrides
      );

      const errorCount = result.summary?.errors || 0;
      const successCount = (result.summary?.created || 0) + (result.summary?.updated || 0);

      setState((prev) => ({
        ...prev,
        step: 'complete',
        importResult: {
          created: result.summary?.created || 0,
          updated: result.summary?.updated || 0,
          skipped: result.summary?.skipped || 0,
          errors: errorCount,
          importId: result.historyId,
          errorDetails: result.errors || []
        }
      }));

      // Show appropriate notification based on result
      if (errorCount > 0 && successCount === 0) {
        showToast.error('Import failed - no records were imported');
      } else if (errorCount > 0) {
        showToast.warning(`Import completed with ${errorCount} error(s)`);
      } else {
        showToast.success('Import completed successfully!');
      }
    } catch (err) {
      const error = err as ApiError;
      const errorMessage =
        (typeof error?.response?.data?.error === 'string' ? error?.response?.data?.error : null) ||
        error?.response?.data?.message ||
        error?.message ||
        'Import failed';
      setState((prev) => ({
        ...prev,
        step: 'error',
        error: errorMessage
      }));
      showToast.error(errorMessage);
    }
  }, [state.preview, rowOverrides]);

  const handleReset = useCallback(() => {
    setState({
      step: 'upload',
      file: null,
      preview: null,
      error: null,
      importResult: null
    });
    setFilter('all');
    setRowOverrides({});
  }, []);

  const handleBackToUpload = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'upload',
      preview: null,
      error: null
    }));
  }, []);

  // Render step progress indicator
  const renderStepProgress = () => {
    const currentStep = getCurrentStepNumber();

    return (
      <div className={styles.importSteps} data-test-id="import-steps">
        {STEPS.map((step, index) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.number < currentStep;
          const isError = state.step === 'error' && step.number === currentStep;

          return (
            <React.Fragment key={step.key}>
              <div
                className={clsx(styles.importStep, {
                  [styles.importStepActive]: isActive,
                  [styles.importStepCompleted]: isCompleted,
                  [styles.importStepError]: isError
                })}
                data-test-id={`import-step-${step.number}`}
              >
                <div className={styles.importStepNumber}>
                  {(() => {
                    if (isCompleted) return <CheckCircle size={20} />;
                    if (isError) return <XCircle size={20} />;
                    return step.number;
                  })()}
                </div>
                <div className={styles.importStepLabel}>{step.label}</div>
              </div>
              {index < STEPS.length - 1 && (
                <div className={clsx(styles.importStepConnector, {
                  [styles.importStepConnectorCompleted]: isCompleted
                })} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Render upload step
  const renderUploadStep = () => (
    <div className={styles.manualImportSection}>
      <div className={styles.manualImportSectionHeader}>
        <Upload size={18} />
        Upload CSV File
      </div>
      <p className={styles.manualImportSectionDescription}>
        Upload a CSV file containing contract and supplier data. The file will be validated before import.
      </p>
      <FileUploadZone
        onFileSelect={handleFileSelect}
        onFileRemove={handleFileRemove}
        selectedFile={state.file}
        error={state.error || undefined}
        isProcessing={state.step === 'validating'}
        showDownloadTemplate={true}
      />
    </div>
  );

  // Render validating state
  const renderValidatingState = () => (
    <div className={styles.manualImportLoading}>
      <Loader2 size={48} className="spin" />
      <h3>Validating your file...</h3>
      <p>Checking data format, validating fields, and detecting conflicts.</p>
    </div>
  );

  // Render preview step
  const renderPreviewStep = () => {
    if (!state.preview) return null;

    const { summary } = state.preview;
    const effectiveCounts = getEffectiveCounts();
    const allErrorsSkipped = areAllErrorsSkipped();
    const canImport = allErrorsSkipped && effectiveCounts.createCount + effectiveCounts.updateCount > 0;

    return (
      <>
        {/* File info bar */}
        <div className={styles.manualImportFileBar} data-test-id="import-file-bar">
          <div className={styles.manualImportFileInfo}>
            <FileCheck size={20} />
            <span className={styles.manualImportFileName}>{state.file?.name}</span>
            <span className={styles.manualImportFileRows}>{summary.totalRows} rows</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleBackToUpload}>
            <ArrowLeft size={16} />
            Change File
          </Button>
        </div>

        {/* Validation Summary */}
        <div className={styles.manualImportSection}>
          <div className={styles.manualImportSectionHeader}>
            <FileCheck size={18} />
            Validation Results
          </div>
          <ValidationSummary summary={summary} activeFilter={filter} onFilterChange={setFilter} />
        </div>

        {/* New Reference Data Warning */}
        {state.preview.newReferenceData.length > 0 && (
          <div
            className={clsx(styles.manualImportInfoBox, styles.manualImportInfoBoxInfo)}
            data-test-id="new-reference-data-warning"
          >
            <AlertTriangle size={18} />
            <div>
              <strong>New reference data will be added:</strong>
              <ul>
                {state.preview.newReferenceData.map((item) => (
                  <li key={`${item.type}-${item.values.join(',')}`}>
                    {item.type}: {item.values.join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Preview Table */}
        <div className={styles.manualImportSection}>
          <div className={styles.manualImportSectionHeader}>
            <Play size={18} />
            Data Preview
          </div>
          <ImportPreviewTable
            rows={state.preview.rows}
            filter={filter}
            rowOverrides={rowOverrides}
            onActionChange={handleActionChange}
          />
        </div>

        {/* Import Actions */}
        <div className={styles.importActions}>
          <div className={styles.importActionsSummary}>
            {canImport ? (
              <>
                Ready to import: <strong className="text-success">{effectiveCounts.createCount}</strong> create,{' '}
                <strong className="text-info">{effectiveCounts.updateCount}</strong> update
                {effectiveCounts.skipCount > 0 && (
                  <>
                    , <strong className="text-muted">{effectiveCounts.skipCount}</strong> skip
                  </>
                )}
              </>
            ) : (
              <span className="text-error">
                <XCircle size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {summary.errorCount > 0
                  ? `Cannot import: errors must be fixed or skipped`
                  : 'No records selected for import'}
              </span>
            )}
          </div>
          <div className={styles.importActionsButtons}>
            <Button variant="secondary" onClick={handleReset}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleStartImport} disabled={!canImport}>
              <Download size={16} />
              Import {effectiveCounts.createCount + effectiveCounts.updateCount} Records
            </Button>
          </div>
        </div>
      </>
    );
  };

  // Render importing state
  const renderImportingState = () => (
    <div className={styles.manualImportLoading} data-test-id="import-loading">
      <Loader2 size={48} className="spin" />
      <h3>Importing data...</h3>
      <p>Creating contracts, updating suppliers, and linking users.</p>
      <div className={styles.manualImportProgressBar}>
        <div className={styles.manualImportProgressBarFill} />
      </div>
    </div>
  );

  // Render complete state
  const renderCompleteState = () => {
    if (!state.importResult) return null;

    const { created, updated, skipped, errors, importId } = state.importResult;
    const successCount = created + updated;
    const hasErrors = errors > 0;
    const isPartialSuccess = hasErrors && successCount > 0;
    const isCompleteFailure = hasErrors && successCount === 0;

    // Determine icon, title, and message based on result
    const getResultDisplay = () => {
      if (isCompleteFailure) {
        return {
          icon: <XCircle size={64} className="text-error" />,
          title: 'Import Failed',
          subtitle: 'No records were imported due to errors.',
          className: 'manual-import__complete--error'
        };
      }
      if (isPartialSuccess) {
        return {
          icon: <AlertTriangle size={64} className="text-warning" />,
          title: 'Import Completed with Errors',
          subtitle: `${successCount} record(s) imported successfully. ${errors} record(s) failed.`,
          className: 'manual-import__complete--warning'
        };
      }
      return {
        icon: <CheckCircle size={64} className="text-success" />,
        title: 'Import Complete!',
        subtitle: 'Your data has been successfully imported.',
        className: 'manual-import__complete--success'
      };
    };

    const display = getResultDisplay();

    // Determine data-test-id based on result status
    const getTestId = () => {
      if (isCompleteFailure) return 'import-complete-failure';
      if (isPartialSuccess) return 'import-complete-partial';
      return 'import-complete-success';
    };

    return (
      <div className={clsx(styles.manualImportComplete, {
        [styles.manualImportCompleteSuccess]: display.className === 'manual-import__complete--success',
        [styles.manualImportCompleteWarning]: display.className === 'manual-import__complete--warning',
        [styles.manualImportCompleteError]: display.className === 'manual-import__complete--error'
      })} data-test-id={getTestId()}>
        <div className={styles.manualImportCompleteIcon}>{display.icon}</div>
        <h2>{display.title}</h2>
        <p className={styles.manualImportCompleteSubtitle}>{display.subtitle}</p>

        <div className={styles.manualImportCompleteStats} data-test-id="import-stats">
          <div className={clsx(styles.manualImportStat, styles.manualImportStatSuccess)} data-test-id="stat-created">
            <span className={styles.manualImportStatValue}>{created}</span>
            <span className={styles.manualImportStatLabel}>Created</span>
          </div>
          <div className={clsx(styles.manualImportStat, styles.manualImportStatInfo)} data-test-id="stat-updated">
            <span className={styles.manualImportStatValue}>{updated}</span>
            <span className={styles.manualImportStatLabel}>Updated</span>
          </div>
          {skipped > 0 && (
            <div className={clsx(styles.manualImportStat, styles.manualImportStatMuted)} data-test-id="stat-skipped">
              <span className={styles.manualImportStatValue}>{skipped}</span>
              <span className={styles.manualImportStatLabel}>Skipped</span>
            </div>
          )}
          {errors > 0 && (
            <div className={clsx(styles.manualImportStat, styles.manualImportStatError)} data-test-id="stat-errors">
              <span className={styles.manualImportStatValue}>{errors}</span>
              <span className={styles.manualImportStatLabel}>Errors</span>
            </div>
          )}
        </div>

        {/* Show detailed errors if any */}
        {state.importResult?.errorDetails && state.importResult.errorDetails.length > 0 && (
          <div className={styles.manualImportErrorDetails} data-test-id="error-details">
            <h3>Error Details:</h3>
            <ul>
              {state.importResult.errorDetails.map((error) => (
                <li key={`error-${error.error}`}>{error.error}</li>
              ))}
            </ul>
          </div>
        )}

        {importId && (
          <p className={styles.manualImportCompleteId}>Import ID: #{importId} • View details in Import History</p>
        )}

        <div className={styles.manualImportCompleteActions}>
          <Button variant="primary" onClick={handleReset}>
            <RefreshCw size={16} />
            Start New Import
          </Button>
        </div>
      </div>
    );
  };

  // Render error state
  const renderErrorState = () => (
    <div className={styles.manualImportError}>
      <div className={styles.manualImportErrorIcon}>
        <XCircle size={64} />
      </div>
      <h2>Import Failed</h2>
      <p className={styles.manualImportErrorMessage}>{state.error || 'An unexpected error occurred during import.'}</p>
      <div className={styles.manualImportErrorActions}>
        <Button variant="secondary" onClick={handleReset}>
          Start Over
        </Button>
        {state.preview && (
          <Button variant="primary" onClick={() => setState((prev) => ({ ...prev, step: 'preview', error: null }))}>
            Back to Preview
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.manualImport} data-test-id="manual-import">
      {/* Step Progress */}
      {renderStepProgress()}

      {/* Content */}
      <div className={styles.manualImportContent}>
        {state.step === 'upload' && renderUploadStep()}
        {state.step === 'validating' && (
          <>
            {renderUploadStep()}
            {renderValidatingState()}
          </>
        )}
        {state.step === 'preview' && renderPreviewStep()}
        {state.step === 'importing' && renderImportingState()}
        {state.step === 'complete' && renderCompleteState()}
        {state.step === 'error' && renderErrorState()}
      </div>
    </div>
  );
};

export default ManualImport;
